import { getCodeCompletion, analyzeCode, recognizePatterns, generateDocumentation } from '../services/api';
import { toast } from 'sonner';

export interface TestCase {
  id: string;
  name: string;
  description: string;
  type: 'unit' | 'integration' | 'e2e' | 'performance';
  code: string;
  expectedResult: any;
  assertions: TestAssertion[];
  aiGenerated: boolean;
  confidence: number;
  tags: string[];
}

export interface TestAssertion {
  type: 'equal' | 'contain' | 'throw' | 'resolve' | 'performance';
  expected: any;
  actual?: any;
  passed?: boolean;
  message?: string;
}

export interface TestSuite {
  id: string;
  name: string;
  description: string;
  testCases: TestCase[];
  coverage: {
    statements: number;
    branches: number;
    functions: number;
    lines: number;
  };
  status: 'pending' | 'running' | 'passed' | 'failed';
  duration: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface DebugSession {
  id: string;
  code: string;
  language: string;
  breakpoints: DebugBreakpoint[];
  variables: DebugVariable[];
  callStack: CallStackFrame[];
  status: 'active' | 'paused' | 'terminated';
  currentLine: number;
  aiSuggestions: DebugSuggestion[];
}

export interface DebugBreakpoint {
  id: string;
  line: number;
  column: number;
  enabled: boolean;
  condition?: string;
  hitCount: number;
}

export interface DebugVariable {
  name: string;
  value: any;
  type: string;
  scope: 'local' | 'global' | 'closure';
}

export interface CallStackFrame {
  functionName: string;
  fileName: string;
  lineNumber: number;
  columnNumber: number;
}

export interface DebugSuggestion {
  type: 'breakpoint' | 'watch' | 'step' | 'fix';
  description: string;
  confidence: number;
  action: () => void;
}

export interface CodeQualityMetrics {
  complexity: number;
  maintainability: number;
  testability: number;
  security: number;
  performance: number;
  overall: number;
  issues: CodeIssue[];
  suggestions: string[];
}

export interface CodeIssue {
  severity: 'low' | 'medium' | 'high' | 'critical';
  category: 'security' | 'performance' | 'maintainability' | 'bug' | 'style';
  message: string;
  line: number;
  column: number;
  suggestion?: string;
}

export class AdvancedTestingService {
  private testSuites: Map<string, TestSuite> = new Map();
  private debugSessions: Map<string, DebugSession> = new Map();
  private qualityMetrics: Map<string, CodeQualityMetrics> = new Map();

  // ===== TESTING FRAMEWORK =====

  async generateTests(code: string, language: string, context?: string): Promise<TestCase[]> {
    try {
      const analysis = await analyzeCode({
        code,
        language,
        analysisType: 'quality'
      });

      const patterns = await recognizePatterns(code, language);

      const testCases: TestCase[] = [];

      // Generate unit tests based on function analysis
      const functions = this.extractFunctions(code, language);
      for (const func of functions) {
        const unitTest = await this.generateUnitTest(func, language, analysis);
        if (unitTest) testCases.push(unitTest);
      }

      // Generate integration tests based on patterns
      if (patterns.patterns && patterns.patterns.length > 0) {
        const integrationTest = await this.generateIntegrationTest(code, language, patterns);
        if (integrationTest) testCases.push(integrationTest);
      }

      // Generate performance tests for complex functions
      if (analysis.score < 70) {
        const performanceTest = await this.generatePerformanceTest(code, language);
        if (performanceTest) testCases.push(performanceTest);
      }

      return testCases;
    } catch (error) {
      console.error('Test generation failed:', error);
      toast.error('Failed to generate tests');
      return [];
    }
  }

  async runTestSuite(suiteId: string): Promise<TestSuite> {
    const suite = this.testSuites.get(suiteId);
    if (!suite) throw new Error('Test suite not found');

    suite.status = 'running';
    suite.updatedAt = new Date();
    const startTime = Date.now();

    try {
      for (const testCase of suite.testCases) {
        await this.runTestCase(testCase);
      }

      suite.duration = Date.now() - startTime;
      suite.status = suite.testCases.every(tc => tc.assertions.every(a => a.passed)) ? 'passed' : 'failed';

      // Update coverage (would integrate with actual test runner)
      suite.coverage = await this.calculateCoverage(suite);

    } catch (error) {
      suite.status = 'failed';
      suite.duration = Date.now() - startTime;
    }

    this.testSuites.set(suiteId, suite);
    return suite;
  }

  private async runTestCase(testCase: TestCase): Promise<void> {
    // Simulate test execution (would integrate with actual test runner)
    for (const assertion of testCase.assertions) {
      try {
        assertion.passed = this.evaluateAssertion(assertion);
      } catch (error) {
        assertion.passed = false;
        assertion.message = error instanceof Error ? error.message : 'Unknown error';
      }
    }
  }

  private evaluateAssertion(assertion: TestAssertion): boolean {
    switch (assertion.type) {
      case 'equal':
        return assertion.actual === assertion.expected;
      case 'contain':
        return Array.isArray(assertion.actual)
          ? assertion.actual.includes(assertion.expected)
          : String(assertion.actual).includes(String(assertion.expected));
      case 'throw':
        // Would check if code throws expected error
        return true; // Placeholder
      case 'performance':
        return assertion.actual < assertion.expected; // Lower is better for performance
      default:
        return false;
    }
  }

  private async calculateCoverage(suite: TestSuite): Promise<TestSuite['coverage']> {
    // Simulate coverage calculation
    return {
      statements: 85,
      branches: 78,
      functions: 92,
      lines: 87
    };
  }

  // ===== DEBUGGING SYSTEM =====

  async startDebugSession(code: string, language: string): Promise<DebugSession> {
    const session: DebugSession = {
      id: `debug_${Date.now()}`,
      code,
      language,
      breakpoints: [],
      variables: [],
      callStack: [],
      status: 'active',
      currentLine: 0,
      aiSuggestions: []
    };

    // Analyze code for suggested breakpoints
    session.aiSuggestions = await this.generateDebugSuggestions(code, language);

    this.debugSessions.set(session.id, session);
    return session;
  }

  async addBreakpoint(sessionId: string, line: number, column: number, condition?: string): Promise<void> {
    const session = this.debugSessions.get(sessionId);
    if (!session) throw new Error('Debug session not found');

    const breakpoint: DebugBreakpoint = {
      id: `bp_${Date.now()}`,
      line,
      column,
      enabled: true,
      condition,
      hitCount: 0
    };

    session.breakpoints.push(breakpoint);
  }

  async generateDebugSuggestions(code: string, language: string): Promise<DebugSuggestion[]> {
    const suggestions: DebugSuggestion[] = [];

    try {
      // Analyze code for debugging opportunities
      const analysis = await analyzeCode({ code, language, analysisType: 'quality' });

      if (analysis.issues) {
        for (const issue of analysis.issues.filter(i => i.severity === 'high')) {
          suggestions.push({
            type: 'breakpoint',
            description: `Add breakpoint at line ${issue.line} to investigate: ${issue.message}`,
            confidence: 0.8,
            action: () => console.log(`Breakpoint added at line ${issue.line}`)
          });
        }
      }

      // Suggest watch expressions for complex variables
      const variables = this.extractVariables(code, language);
      for (const variable of variables.slice(0, 3)) {
        suggestions.push({
          type: 'watch',
          description: `Watch variable '${variable}' for changes`,
          confidence: 0.6,
          action: () => console.log(`Watching variable: ${variable}`)
        });
      }

    } catch (error) {
      console.error('Debug suggestion generation failed:', error);
    }

    return suggestions;
  }

  // ===== CODE QUALITY ANALYSIS =====

  async analyzeCodeQuality(code: string, language: string): Promise<CodeQualityMetrics> {
    const cacheKey = `quality_${code.length}_${language}`;

    if (this.qualityMetrics.has(cacheKey)) {
      return this.qualityMetrics.get(cacheKey)!;
    }

    try {
      const analysis = await analyzeCode({ code, language, analysisType: 'quality' });
      const patterns = await recognizePatterns(code, language);

      const metrics: CodeQualityMetrics = {
        complexity: this.calculateComplexity(code, language),
        maintainability: Math.max(0, Math.min(100, analysis.score || 50)),
        testability: this.calculateTestability(code, language),
        security: this.calculateSecurityScore(code, language),
        performance: this.calculatePerformanceScore(code, language),
        overall: 0,
        issues: this.convertIssues(analysis.issues || [], patterns.antiPatterns || []),
        suggestions: analysis.recommendations || []
      };

      metrics.overall = Math.round(
        (metrics.complexity + metrics.maintainability + metrics.testability + metrics.security + metrics.performance) / 5
      );

      this.qualityMetrics.set(cacheKey, metrics);
      return metrics;

    } catch (error) {
      console.error('Quality analysis failed:', error);
      return this.getDefaultMetrics();
    }
  }

  // ===== REFACTORING TOOLS =====

  async suggestRefactoring(code: string, language: string): Promise<{
    refactorings: Array<{
      type: string;
      description: string;
      confidence: number;
      before: string;
      after: string;
      impact: 'low' | 'medium' | 'high';
    }>;
  }> {
    try {
      const patterns = await recognizePatterns(code, language);
      const analysis = await analyzeCode({ code, language, analysisType: 'quality' });

      const refactorings = [];

      // Extract method refactoring
      if (code.split('\n').length > 50) {
        refactorings.push({
          type: 'extract_method',
          description: 'Extract long function into smaller, focused methods',
          confidence: 0.7,
          before: 'Long function with multiple responsibilities',
          after: 'Multiple focused functions with single responsibilities',
            impact: 'high' as const
        });
      }

      // Variable naming improvements
      const badVariables = this.findBadVariableNames(code);
      if (badVariables.length > 0) {
        refactorings.push({
          type: 'rename_variables',
          description: `Rename ${badVariables.length} variables for better clarity`,
          confidence: 0.8,
          before: badVariables.join(', '),
          after: 'Descriptive variable names',
          impact: 'medium' as const
        });
      }

      // Pattern-based refactorings
      if (patterns.patterns) {
        for (const pattern of patterns.patterns.slice(0, 3)) {
          if (pattern.suggestion) {
            refactorings.push({
              type: 'pattern_refactor',
              description: pattern.suggestion,
              confidence: 0.75,
              before: 'Current implementation',
              after: 'Improved pattern-based implementation',
              impact: 'medium' as const
            });
          }
        }
      }

      return { refactorings };

    } catch (error) {
      console.error('Refactoring suggestion failed:', error);
      return { refactorings: [] };
    }
  }

  // ===== PERFORMANCE PROFILING =====

  async profilePerformance(code: string, language: string): Promise<{
    executionTime: number;
    memoryUsage: number;
    bottlenecks: Array<{
      type: string;
      location: string;
      impact: number;
      suggestion: string;
    }>;
    recommendations: string[];
  }> {
    // Simulate performance profiling
    const executionTime = Math.random() * 100 + 10; // 10-110ms
    const memoryUsage = Math.random() * 50 + 5; // 5-55MB

    const bottlenecks = [];

    // Check for common performance issues
    if (code.includes('for') && code.includes('for')) {
      bottlenecks.push({
        type: 'nested_loops',
        location: 'Multiple nested for loops detected',
        impact: 0.7,
        suggestion: 'Consider optimizing nested loops or using more efficient algorithms'
      });
    }

    if (code.includes('new Array') && code.includes('push')) {
      bottlenecks.push({
        type: 'array_operations',
        location: 'Frequent array operations',
        impact: 0.5,
        suggestion: 'Consider using more efficient data structures or pre-allocating arrays'
      });
    }

    const recommendations = [
      'Consider using more efficient algorithms for large datasets',
      'Profile memory usage and optimize object creation',
      'Use caching for expensive computations',
      'Consider lazy loading for large code sections'
    ];

    return {
      executionTime,
      memoryUsage,
      bottlenecks,
      recommendations
    };
  }

  // ===== PHASE 7.2 WEEK 2: ADVANCED AI REASONING =====

  async analyzeMultiFileDependencies(code: string, language: string, projectFiles: string[]): Promise<{
    dependencies: Array<{
      file: string;
      type: 'import' | 'require' | 'extend' | 'implement';
      line: number;
      confidence: number;
    }>;
    circularDependencies: Array<{
      files: string[];
      severity: 'warning' | 'error';
      impact: string;
    }>;
    missingDependencies: string[];
    unusedImports: Array<{
      file: string;
      line: number;
      importName: string;
    }>;
  }> {
    try {
      const dependencies = [];
      const circularDeps = [];
      const missingDeps = [];
      const unusedImports = [];

      // Analyze import/export patterns
      for (const file of projectFiles) {
        if (file.includes('.json') || file.includes('package.json')) continue;

        const importPatterns = this.extractImports(code, language);
        for (const imp of importPatterns) {
          // Check if imported file exists in project
          const importedFile = projectFiles.find(f =>
            f.includes(imp.moduleName) ||
            f.replace(/\.[^/.]+$/, '') === imp.moduleName
          );

          if (!importedFile) {
            missingDeps.push(`${imp.moduleName} (imported at line ${imp.line})`);
          } else {
            dependencies.push({
              file: importedFile,
              type: imp.type as any,
              line: imp.line,
              confidence: 0.9
            });
          }
        }
      }

      // Detect circular dependencies
      const dependencyGraph = this.buildDependencyGraph(dependencies);
      const cycles = this.detectCircularDependencies(dependencyGraph);
      for (const cycle of cycles) {
        circularDeps.push({
          files: cycle,
          severity: 'warning' as const,
          impact: 'May cause runtime issues and slow compilation'
        });
      }

      // Find unused imports (simplified)
      for (const dep of dependencies) {
        const importedName = this.extractImportedName(dep.file);
        const usageCount = (code.match(new RegExp(`\\b${importedName}\\b`, 'g')) || []).length;
        if (usageCount <= 1) { // Only the import statement
          unusedImports.push({
            file: dep.file,
            line: dep.line,
            importName: importedName
          });
        }
      }

      return {
        dependencies,
        circularDependencies: circularDeps,
        missingDependencies: missingDeps,
        unusedImports
      };

    } catch (error) {
      console.error('Multi-file dependency analysis failed:', error);
      return {
        dependencies: [],
        circularDependencies: [],
        missingDependencies: [],
        unusedImports: []
      };
    }
  }

  async predictCodeIssues(code: string, language: string, historicalData: any[]): Promise<{
    predictions: Array<{
      type: 'bug' | 'performance' | 'security' | 'maintainability';
      description: string;
      probability: number;
      prevention: string;
      similarCases: number;
    }>;
    riskScore: number;
    recommendations: string[];
  }> {
    try {
      const predictions = [];
      let riskScore = 0;

      // Analyze based on historical patterns
      const similarPatterns = historicalData.filter(h =>
        h.language === language &&
        h.complexity > this.calculateComplexity(code, language) * 0.8
      );

      // Predict potential bugs based on patterns
      if (code.includes('async') && !code.includes('await')) {
        predictions.push({
          type: 'bug' as const,
          description: 'Potential unhandled promise in async function',
          probability: 0.75,
          prevention: 'Ensure all async operations use await or return promises properly',
          similarCases: similarPatterns.filter(p => p.issues?.includes('unhandled_promise')).length
        });
        riskScore += 15;
      }

      // Predict performance issues
      if (code.includes('for') && code.includes('.push(')) {
        predictions.push({
          type: 'performance' as const,
          description: 'Array push in loop may cause performance degradation',
          probability: 0.65,
          prevention: 'Consider pre-allocating array size or using more efficient data structures',
          similarCases: similarPatterns.filter(p => p.issues?.includes('array_performance')).length
        });
        riskScore += 10;
      }

      // Predict security issues
      if (code.includes('eval(') || code.includes('innerHTML')) {
        predictions.push({
          type: 'security' as const,
          description: 'Potential security vulnerability with dynamic code execution',
          probability: 0.85,
          prevention: 'Avoid eval() and innerHTML. Use safer alternatives like textContent',
          similarCases: similarPatterns.filter(p => p.issues?.includes('security_vulnerability')).length
        });
        riskScore += 25;
      }

      // Predict maintainability issues
      const functionCount = this.extractFunctions(code, language).length;
      if (functionCount > 10) {
        predictions.push({
          type: 'maintainability' as const,
          description: 'High function count may indicate complex, hard-to-maintain code',
          probability: 0.7,
          prevention: 'Consider breaking down into smaller, focused modules or classes',
          similarCases: similarPatterns.filter(p => p.functionCount > 10).length
        });
        riskScore += 15;
      }

      const recommendations = [
        'Review high-probability predictions first',
        'Implement automated testing for predicted issues',
        'Consider code reviews for high-risk patterns',
        'Monitor similar patterns in future development'
      ];

      return {
        predictions,
        riskScore: Math.min(100, riskScore),
        recommendations
      };

    } catch (error) {
      console.error('Code issue prediction failed:', error);
      return {
        predictions: [],
        riskScore: 0,
        recommendations: ['Unable to analyze code patterns at this time']
      };
    }
  }

  async optimizePerformance(code: string, language: string): Promise<{
    optimizations: Array<{
      type: string;
      description: string;
      before: string;
      after: string;
      performanceGain: number; // percentage
      confidence: number;
    }>;
    overallImpact: number;
    implementationEffort: 'low' | 'medium' | 'high';
  }> {
    try {
      const optimizations = [];
      let overallImpact = 0;

      // Algorithm optimizations
      if (code.includes('O(n²)') || (code.includes('for') && code.includes('for'))) {
        optimizations.push({
          type: 'algorithm',
          description: 'Replace nested loops with more efficient algorithm',
          before: 'Nested O(n²) loops',
          after: 'Optimized O(n) or O(n log n) algorithm',
          performanceGain: 80,
          confidence: 0.8
        });
        overallImpact += 80;
      }

      // Memory optimizations
      if (code.includes('.push(') && code.includes('for')) {
        optimizations.push({
          type: 'memory',
          description: 'Pre-allocate array size to avoid dynamic resizing',
          before: 'Dynamic array.push() in loop',
          after: 'Pre-allocated array with known size',
          performanceGain: 30,
          confidence: 0.9
        });
        overallImpact += 30;
      }

      // String concatenation optimization
      if (code.includes('+') && code.includes('for') && code.includes('"')) {
        optimizations.push({
          type: 'string',
          description: 'Use array.join() instead of string concatenation in loops',
          before: 'String concatenation in loop',
          after: 'Array accumulation with join()',
          performanceGain: 60,
          confidence: 0.85
        });
        overallImpact += 60;
      }

      // DOM manipulation optimization
      if (code.includes('document.') && code.includes('for')) {
        optimizations.push({
          type: 'dom',
          description: 'Batch DOM operations to reduce reflows',
          before: 'Individual DOM updates in loop',
          after: 'DocumentFragment or batched updates',
          performanceGain: 70,
          confidence: 0.75
        });
        overallImpact += 70;
      }

      // Caching optimizations
      const functionCalls = (code.match(/\w+\(/g) || []).length;
      if (functionCalls > 20) {
        optimizations.push({
          type: 'caching',
          description: 'Implement result caching for expensive function calls',
          before: 'Repeated expensive function calls',
          after: 'Memoized functions with caching',
          performanceGain: 50,
          confidence: 0.7
        });
        overallImpact += 50;
      }

      const implementationEffort = optimizations.length > 3 ? 'high' :
                                 optimizations.length > 1 ? 'medium' : 'low';

      return {
        optimizations,
        overallImpact: Math.min(95, overallImpact),
        implementationEffort
      };

    } catch (error) {
      console.error('Performance optimization analysis failed:', error);
      return {
        optimizations: [],
        overallImpact: 0,
        implementationEffort: 'low'
      };
    }
  }

  // ===== HELPER METHODS =====

  private extractFunctions(code: string, language: string): Array<{ name: string; body: string; params: string[] }> {
    // Simple function extraction (would be more sophisticated in production)
    const functions = [];
    const lines = code.split('\n');

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      if (language === 'javascript' || language === 'typescript') {
        if (line.startsWith('function ') || line.includes('=>') || line.match(/^\s*\w+\s*\(/)) {
          functions.push({
            name: this.extractFunctionName(line, language),
            body: this.extractFunctionBody(lines, i),
            params: this.extractParameters(line, language)
          });
        }
      }
    }

    return functions;
  }

  private extractFunctionName(line: string, language: string): string {
    // Simple name extraction
    if (line.includes('function ')) {
      return line.split('function ')[1].split('(')[0].trim();
    }
    if (line.includes('=>')) {
      return 'arrow_function';
    }
    return 'anonymous_function';
  }

  private extractFunctionBody(lines: string[], startIndex: number): string {
    // Simple body extraction
    let braceCount = 0;
    let endIndex = startIndex;

    for (let i = startIndex; i < lines.length; i++) {
      const line = lines[i];
      braceCount += (line.match(/\{/g) || []).length;
      braceCount -= (line.match(/\}/g) || []).length;

      if (braceCount === 0 && line.includes('}')) {
        endIndex = i;
        break;
      }
    }

    return lines.slice(startIndex, endIndex + 1).join('\n');
  }

  private extractParameters(line: string, language: string): string[] {
    const paramMatch = line.match(/\(([^)]*)\)/);
    if (paramMatch) {
      return paramMatch[1].split(',').map(p => p.trim()).filter(p => p);
    }
    return [];
  }

  private async generateUnitTest(func: any, language: string, analysis: any): Promise<TestCase | null> {
    try {
      // Use AI to generate test case
      const testPrompt = `Generate a unit test for this ${language} function:
${func.body}

Function name: ${func.name}
Parameters: ${func.params.join(', ')}

Please generate a comprehensive unit test.`;

      const result = await getCodeCompletion({
        code: testPrompt,
        cursorPosition: testPrompt.length,
        language: 'javascript', // Generate test in JS
        context: 'test_generation'
      });

      if (result.suggestions && result.suggestions.length > 0) {
        return {
          id: `test_${func.name}_${Date.now()}`,
          name: `Unit test for ${func.name}`,
          description: `Test ${func.name} function with various inputs`,
          type: 'unit',
          code: result.suggestions[0].code,
          expectedResult: 'Test should pass',
          assertions: [{
            type: 'equal',
            expected: true,
            message: 'Function should execute without errors'
          }],
          aiGenerated: true,
          confidence: result.suggestions[0].confidence,
          tags: ['unit', 'ai-generated', func.name]
        };
      }

    } catch (error) {
      console.error('Unit test generation failed:', error);
    }

    return null;
  }

  private async generateIntegrationTest(code: string, language: string, patterns: any): Promise<TestCase | null> {
    // Generate integration test based on patterns
    return {
      id: `integration_${Date.now()}`,
      name: 'Integration Test Suite',
      description: 'Test component integration and data flow',
      type: 'integration',
      code: `// Integration test for ${language} code\nconsole.log('Integration test placeholder');`,
      expectedResult: 'All components should work together',
      assertions: [{
        type: 'equal',
        expected: true,
        message: 'Integration should complete successfully'
      }],
      aiGenerated: true,
      confidence: 0.7,
      tags: ['integration', 'ai-generated']
    };
  }

  private async generatePerformanceTest(code: string, language: string): Promise<TestCase | null> {
    return {
      id: `perf_${Date.now()}`,
      name: 'Performance Test',
      description: 'Test code performance under load',
      type: 'performance',
      code: `// Performance test for ${language} code\nconsole.time('execution');\n// Execute code multiple times\nconsole.timeEnd('execution');`,
      expectedResult: 'Execution should complete within time limits',
      assertions: [{
        type: 'performance',
        expected: 1000, // 1 second max
        message: 'Performance should be within acceptable limits'
      }],
      aiGenerated: true,
      confidence: 0.6,
      tags: ['performance', 'ai-generated']
    };
  }

  private extractVariables(code: string, language: string): string[] {
    const variables = new Set<string>();

    if (language === 'javascript' || language === 'typescript') {
      // Extract variable declarations
      const varMatches = code.match(/\b(?:const|let|var)\s+(\w+)/g);
      if (varMatches) {
        varMatches.forEach(match => {
          const varName = match.split(/\s+/)[1];
          variables.add(varName);
        });
      }
    }

    return Array.from(variables);
  }

  private extractImports(code: string, language: string): Array<{
    moduleName: string;
    type: 'import' | 'require';
    line: number;
  }> {
    const imports: Array<{
      moduleName: string;
      type: 'import' | 'require';
      line: number;
    }> = [];

    if (language === 'javascript' || language === 'typescript') {
      // ES6 imports
      const es6Matches = code.match(/import\s+.*?\s+from\s+['"]([^'"]+)['"]/g);
      if (es6Matches) {
        es6Matches.forEach((match, index) => {
          const moduleMatch = match.match(/from\s+['"]([^'"]+)['"]/);
          if (moduleMatch) {
            imports.push({
              moduleName: moduleMatch[1],
              type: 'import',
              line: code.substring(0, code.indexOf(match)).split('\n').length
            });
          }
        });
      }

      // CommonJS requires
      const cjsMatches = code.match(/require\s*\(\s*['"]([^'"]+)['"]\s*\)/g);
      if (cjsMatches) {
        cjsMatches.forEach((match, index) => {
          const moduleMatch = match.match(/['"]([^'"]+)['"]/);
          if (moduleMatch) {
            imports.push({
              moduleName: moduleMatch[1],
              type: 'require',
              line: code.substring(0, code.indexOf(match)).split('\n').length
            });
          }
        });
      }
    }

    return imports;
  }

  private buildDependencyGraph(dependencies: any[]): Map<string, string[]> {
    const graph = new Map<string, string[]>();

    dependencies.forEach(dep => {
      if (!graph.has(dep.file)) {
        graph.set(dep.file, []);
      }
      // For simplicity, we'll track reverse dependencies
      // In a real implementation, this would be more sophisticated
    });

    return graph;
  }

  private detectCircularDependencies(graph: Map<string, string[]>): string[][] {
    // Simplified circular dependency detection
    // In production, this would use proper graph algorithms
    const cycles: string[][] = [];

    // Placeholder implementation
    return cycles;
  }

  private extractImportedName(filePath: string): string {
    // Extract the module name from file path
    const parts = filePath.split('/');
    const fileName = parts[parts.length - 1];
    return fileName.replace(/\.[^/.]+$/, ''); // Remove extension
  }

  private calculateComplexity(code: string, language: string): number {
    let complexity = 1; // Base complexity

    // Count control structures
    const controls = (code.match(/\b(if|for|while|switch|catch)\b/g) || []).length;
    complexity += controls * 0.5;

    // Count functions/methods
    const functions = (code.match(/\bfunction\b|\b=>\b|\bclass\b/g) || []).length;
    complexity += functions * 0.3;

    // Count nesting levels (simplified)
    const lines = code.split('\n');
    let maxNesting = 0;
    let currentNesting = 0;

    for (const line of lines) {
      const opens = (line.match(/\{/g) || []).length;
      const closes = (line.match(/\}/g) || []).length;
      currentNesting += opens - closes;
      maxNesting = Math.max(maxNesting, currentNesting);
    }

    complexity += maxNesting * 0.2;

    return Math.min(100, Math.round(complexity * 10));
  }

  private calculateTestability(code: string, language: string): number {
    let score = 50; // Base score

    // Functions with few parameters are more testable
    const functions = this.extractFunctions(code, language);
    if (functions.length > 0) {
      const avgParams = functions.reduce((sum, f) => sum + f.params.length, 0) / functions.length;
      score += (5 - avgParams) * 5; // Fewer params = higher score
    }

    // Pure functions are more testable
    if (!code.includes('Math.random') && !code.includes('Date.now')) {
      score += 20;
    }

    // Dependency injection makes testing easier
    if (code.includes('interface') || code.includes('mock') || code.includes('stub')) {
      score += 15;
    }

    return Math.min(100, Math.max(0, score));
  }

  private calculateSecurityScore(code: string, language: string): number {
    let score = 80; // Base score (assume secure until proven otherwise)

    // Check for security issues
    const securityIssues = [
      /eval\s*\(/,
      /innerHTML\s*=/,
      /document\.write/,
      /setTimeout.*string/,
      /new Function/,
      /SQL.*\+/,
      /password.*string/i
    ];

    for (const pattern of securityIssues) {
      if (pattern.test(code)) {
        score -= 15;
      }
    }

    // Good practices
    if (code.includes('https://') || code.includes('prepared statement')) {
      score += 10;
    }

    return Math.min(100, Math.max(0, score));
  }

  private calculatePerformanceScore(code: string, language: string): number {
    let score = 70; // Base score

    // Check for performance issues
    const perfIssues = [
      /for.*for/, // Nested loops
      /while.*true/, // Infinite loops potential
      /\+=.*string/, // String concatenation in loops
      /new.*\[\]/, // Array creation in loops
    ];

    for (const pattern of perfIssues) {
      if (pattern.test(code)) {
        score -= 10;
      }
    }

    // Good practices
    if (code.includes('const') || code.includes('Map') || code.includes('Set')) {
      score += 10;
    }

    return Math.min(100, Math.max(0, score));
  }

  private convertIssues(analysisIssues: any[], antiPatterns: any[]): CodeIssue[] {
    const issues: CodeIssue[] = [];

    // Convert analysis issues
    if (analysisIssues) {
      for (const issue of analysisIssues) {
        issues.push({
          severity: issue.severity || 'medium',
          category: this.mapCategory(issue.type),
          message: issue.message || 'Code quality issue',
          line: issue.line || 0,
          column: issue.column || 0,
          suggestion: issue.suggestion
        });
      }
    }

    // Convert anti-patterns
    if (antiPatterns) {
      for (const pattern of antiPatterns) {
        issues.push({
          severity: pattern.severity || 'medium',
          category: 'maintainability',
          message: pattern.type || 'Anti-pattern detected',
          line: 0,
          column: 0,
          suggestion: pattern.recommendation
        });
      }
    }

    return issues;
  }

  private mapCategory(type: string): CodeIssue['category'] {
    const categoryMap: Record<string, CodeIssue['category']> = {
      'error': 'bug',
      'warning': 'maintainability',
      'security': 'security',
      'performance': 'performance'
    };

    return categoryMap[type] || 'maintainability';
  }

  private findBadVariableNames(code: string): string[] {
    const badNames = [];
    const varPattern = /\b(?:const|let|var)\s+(\w+)/g;
    let match;

    while ((match = varPattern.exec(code)) !== null) {
      const varName = match[1];
      if (varName.length < 3 || /^\d/.test(varName) || varName.toLowerCase() === varName) {
        badNames.push(varName);
      }
    }

    return badNames;
  }

  private getDefaultMetrics(): CodeQualityMetrics {
    return {
      complexity: 50,
      maintainability: 50,
      testability: 50,
      security: 70,
      performance: 60,
      overall: 56,
      issues: [],
      suggestions: ['Code analysis failed, please try again']
    };
  }
}

export const advancedTestingService = new AdvancedTestingService();